import { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { toast } from 'sonner';
import { Tag, Loader2, Archive } from 'lucide-react';
import { getBearerToken } from '@/context/TrackOticAuthContext';
import { PassportBatchesTable } from '@/pages/admin/qr/PassportBatchesTable';

const API = process.env.REACT_APP_BACKEND_URL;

// Receive-only: passports are issued by Track0tic (admin → QR labels). Tenants see and print what was issued to them.
// Same source as the admin screen (qr.passport_batches / qr.passports). Passports never expire and never close.
export default function LabelInventoryPage() {
  const [stock, setStock] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = getBearerToken();
    fetch(`${API}/api/qr/stock`, { credentials: 'include', headers: token ? { Authorization: `Bearer ${token}` } : {} })
      .then(async r => { if (!r.ok) throw new Error((await r.json()).detail || `Request failed (${r.status})`); return r.json(); })
      .then(setStock).catch(e => toast.error(e.message)).finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 className="w-8 h-8 animate-spin text-teal-500" />
      </div>
    );
  }

  const batches = stock?.batches || [];
  const issuedInBatches = batches.reduce((a, b) => a + (b.count || 0) + (b.parents || 0), 0); // children + carton parents
  const inStock = stock?.issued_total ?? 0;
  const bound = Math.max(0, issuedInBatches - inStock);

  return (
    <div className="p-6 space-y-6" data-testid="label-inventory-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 dark:text-white" data-testid="label-page-title">Label Inventory</h1>
          <p className="text-slate-500 dark:text-slate-400 text-sm">Passport QR labels issued to you by Track0tic. Print at 100% scale.</p>
        </div>
        <span className="inline-flex items-center gap-2 text-xs px-3 py-1.5 rounded-md border border-slate-200 text-slate-500 bg-slate-50" data-testid="label-receive-only-note">
          <Tag className="w-3.5 h-3.5" /> Issued by Track0tic — receive only
        </span>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {[
          ['Batches received', batches.length, 'label-stat-batches'],
          ['Labels issued', issuedInBatches, 'label-stat-issued'],
          ['In stock (unbound)', inStock, 'label-stat-in-stock'],
          ['Bound to a device', bound, 'label-stat-bound'],
        ].map(([label, value, tid]) => (
          <Card key={tid} data-testid={tid}>
            <CardContent className="pt-5 text-center">
              <p className="text-xs text-slate-500">{label}</p>
              <p className="text-2xl font-bold text-slate-900 dark:text-white">{value}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2"><Archive className="w-4 h-4 text-teal-500" /> Batches issued to you</CardTitle>
        </CardHeader>
        <CardContent>
          {batches.length === 0
            ? <p className="text-sm text-slate-500 py-6 text-center" data-testid="label-batches-empty">No passport batches issued yet. Ask your Track0tic account manager.</p>
            : <PassportBatchesTable batches={batches} tenantScoped />}
        </CardContent>
      </Card>
    </div>
  );
}
